import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertController, IonNav } from '@ionic/angular';
import { CaseService } from 'src/app/services/case.service';
import { CommonService } from 'src/app/services/common.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-ezidebit-card',
  templateUrl: './ezidebit-card.page.html',
  styleUrls: ['./ezidebit-card.page.scss'],
})
export class EzidebitCardPage implements OnInit {
  @Input() caseId;
  @Input() responseData;
  @Input() debtorDetails;
  @Input() totalOutstandingWithSymbol;
  @Input() caseDetails;

  EzidebitCardForm: FormGroup;
  saveBtnDisabled = false;
  isConfigured = true;
  company = '';
  amount = '';
  message = '';
  surcharge_amount = 0;
  fieldLabels: any = {};
  paymentGatewayTemplate;
  years: number[] = this.generateYearList();
  months: number[] = this.generateMonthList();
  minPostDate = new Date().toISOString().split('T')[0];

  constructor(
    private formBuilder: FormBuilder,
    private caseService: CaseService,
    private commonService: CommonService,
    private ionNav: IonNav,
    private alertController: AlertController,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.amount = parseFloat(this.responseData?.net_amount).toFixed(2);
    this.company = this.responseData?.company;
    this.surcharge_amount = this.responseData?.surcharge_amount;
    const portalSettings = JSON.parse(localStorage.getItem('portal_settings'));
    if (localStorage.getItem('language') !== 'en') {
      this.message =
        portalSettings['payment_type_message_' + localStorage.getItem('language')] ||
        portalSettings?.payment_type_message;
    } else {
      this.message = portalSettings?.payment_type_message;
    }
    if (portalSettings?.gateway_templates?.ezidebit?.card_non_recurring_payment) {
      this.paymentGatewayTemplate =
        portalSettings.gateway_templates.ezidebit.card_non_recurring_payment;
    }
    if (portalSettings?.fieldLabels) {
      this.fieldLabels = portalSettings.fieldLabels;
    }
    this.initForm();
  }

  initForm() {
    this.EzidebitCardForm = this.formBuilder.group({
      amount: [{ value: this.amount ? this.amount : 0, disabled: true }, [Validators.required]],
      cvv: ['', Validators.required],
      card_number: ['', [Validators.required, Validators.pattern('^[0-9]{12,20}$')]],
      expiry_month: ['', [Validators.required]],
      expiry_year: ['', [Validators.required]],
      post_date: [this.minPostDate, [Validators.required]],
      address_ln1: [
        this.debtorDetails?.addresses[0]?.address_ln1 || '',
        [Validators.required],
      ],
      address_ln2: [this.debtorDetails?.addresses[0]?.address_ln2 || '', []],
      address_ln3: [this.debtorDetails?.addresses[0]?.address_ln3 || '', []],
      address_town: [this.debtorDetails?.addresses[0]?.address_town || '', []],
      address_postcode: [
        this.debtorDetails?.addresses[0]?.address_postcode || '',
        [Validators.required],
      ],
      debtor_name: [this.debtorDetails?.debtor_name || '', [Validators.required]],
      debtor_email: [this.debtorDetails?.debtor_email || '', [Validators.required]],
      acceptTerms: [false],
    });
  }

  generateYearList(): number[] {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 16 }, (_, i) => currentYear + i);
  }

  generateMonthList(): number[] {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }

  async proceedPayment() {
    if (this.EzidebitCardForm.valid) {
      if (
        this.paymentGatewayTemplate?.above_proceed &&
        !this.EzidebitCardForm.get('acceptTerms').value
      ) {
        this.showAlert(this.translate.instant('PAYMENT.AGREE_TO_TERMS'));
        return;
      }
      if (this.responseData?.confirmMessage) {
        const alert = await this.alertController.create({
          header: this.translate.instant('PAYMENT.CONFIRM_AGREEMENT'),
          message: `<ion-item>
                      <ion-label position="stacked">${this.translate.instant('PAYMENT.ENTER_NAME')}</ion-label>
                      <ion-input name="name" type="text" placeholder="${this.translate.instant('PAYMENT.ENTER_NAME')}" required></ion-input>
                    </ion-item>
                    <ion-item lines="none">
                      <ion-checkbox name="agreement" slot="start" value="agree"></ion-checkbox>
                      <ion-label>${this.translate.instant('PAYMENT.AGREED')}</ion-label>
                    </ion-item>`,
          cssClass: 'custom-alert-class',
          buttons: [
            {
              text: this.translate.instant('COMMON.CANCEL'),
              role: 'cancel',
              handler: () => {},
            },
            {
              text: 'OK',
              handler: () => {
                const nameInput = document.querySelector(
                  'ion-input[name="name"]'
                ) as HTMLIonInputElement;
                const agreementCheckbox = document.querySelector(
                  'ion-checkbox[name="agreement"]'
                ) as HTMLIonCheckboxElement;
                if (nameInput?.value && agreementCheckbox?.checked) {
                  this.confirmPayment();
                } else {
                  if (!nameInput?.value) {
                    this.showAlert(this.translate.instant('PAYMENT.ENTER_NAME'));
                  } else {
                    this.showAlert(this.translate.instant('PAYMENT.AGREE_TO_TERMS'));
                  }
                  return false;
                }
              },
            },
          ],
        });
        await alert.present();
      } else {
        this.confirmPayment();
      }
    }
  }

  async showAlert(message: string) {
    const alert = await this.alertController.create({
      header: this.translate.instant('COMMON.ALERT'),
      message,
      buttons: ['OK'],
    });
    await alert.present();
  }

  confirmPayment() {
    this.saveBtnDisabled = true;
    const obj = this.EzidebitCardForm.value;
    obj.debtor_id = this.debtorDetails.debtor_id;
    [obj.debtor_first_name, obj.debtor_last_name] = obj.debtor_name.split(/ (.+)/);
    obj.gateway_id = this.responseData.id;
    const paymentRequest = this.isFuturePayment(obj.post_date)
      ? this.caseService.makeEzidebitFuturePayment(this.caseId, obj)
      : this.caseService.makeEzidebitPayment(this.caseId, obj);
    paymentRequest.subscribe((res: any) => {
      this.saveBtnDisabled = false;
      this.commonService.dismissLoader();
      if (res.result) {
        this.commonService.showToast(res['message'], 'success');
        this.caseService.saveCaseDetails(localStorage.getItem('debtor_id'));
        this.ionNav.popToRoot();
      } else {
        this.commonService.showToast(res['message'], 'danger');
      }
    });
  }

  dismiss() {
    this.ionNav.pop();
  }

  private isFuturePayment(postDate: string): boolean {
    const normalizedPostDate = this.normalizeDate(postDate);
    return !!normalizedPostDate && normalizedPostDate > this.minPostDate;
  }

  private normalizeDate(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    return dateValue.split('T')[0];
  }

  cancel() {
    this.dismiss();
  }
}
